import { useState } from "react";
import { motion } from "framer-motion";
import { Download, Printer, Lock, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";
import { useSite } from "@/context/SiteContext";
import { API, fetchBlob } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const TAPES = ["#ff4d8d", "#38b6ff", "#2fb344", "#ff7a2f"];

const printBlob = (url) => {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<html><head><title>Ananawi</title><style>@page{margin:8mm}body{margin:0;display:flex;justify-content:center}img{max-width:100%;max-height:100vh}</style></head><body><img src="${url}" onload="setTimeout(function(){window.print()},300)"/></body></html>`);
  w.document.close();
};

// Title in the active language; empty translation -> Italian title.
const localTitle = (item, lang) => item.titles?.[lang]?.trim() || item.title;

const ColoringCard = ({ item, i, user, t, lang }) => {
  const title = localTitle(item, lang);
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();
  const act = async (kind) => {
    if (!user) {
      navigate("/login?next=/genitori");
      return;
    }
    setBusy(kind);
    try {
      const url = await fetchBlob(`/coloring/${item.id}/download?action=${kind}`);
      if (kind === "print" && item.content_type?.startsWith("image/")) printBlob(url);
      else if (kind === "print") window.open(url, "_blank");
      else {
        const a = document.createElement("a");
        a.href = url;
        const label = item.item ? [t.games.kitItems?.[item.item], t.games.kitThemes?.[item.theme] ?? item.theme].filter(Boolean).join(" ") : "";
        a.download = `ananawi-${label || item.title}.${item.ext}`;
        a.click();
      }
    } catch {
      toast.error(t.common.error);
    } finally {
      setBusy(null);
    }
  };
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.6, delay: (i % 4) * 0.08 }}
      whileHover={{ rotate: 0, y: -8 }}
      style={{ rotate: i % 2 ? 1.5 : -1.5 }}
      className="relative flex h-full flex-col rounded-[24px] sticker bg-white p-4 pt-6"
      data-testid={`coloring-card-${item.id}`}
    >
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 w-20 h-6 rotate-[-4deg] opacity-90 border-2 border-ink" style={{ background: TAPES[i % 4] }} />
      <div className="aspect-[3/4] overflow-hidden rounded-[14px] border-2 border-ink/20 bg-white grid place-items-center">
        {item.has_thumb && API ? (
          <img src={`${API}/coloring/${item.id}/thumb`} alt={title} loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <FileText className="w-16 h-16 text-ink/40" />
        )}
      </div>
      <h3 className="mt-4 font-display font-semibold text-lg leading-tight">{item.item ? (t.games.kitItems?.[item.item] ?? title) : title}</h3>
      {item.theme && <p className="text-xs font-extrabold text-pink uppercase">{t.games.kitThemes?.[item.theme] ?? item.theme}</p>}
      <div className="mt-auto grid grid-cols-[minmax(0,1fr)_2rem] gap-0.5 pt-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-2">
        <button onClick={() => act("download")} className="min-w-0 h-9 btn-pop btn-pop-sm sticker-sm inline-flex items-center justify-center gap-1 rounded-full bg-leaf text-white px-1 text-[9px] font-extrabold sm:h-auto sm:gap-1.5 sm:px-3 sm:py-2 sm:text-sm" data-testid={`coloring-download-${item.id}`}>
          {busy === "download" ? <Loader2 className="h-3 w-3 animate-spin sm:h-4 sm:w-4" /> : user ? <Download className="h-3 w-3 shrink-0 sm:h-4 sm:w-4" /> : <Lock className="h-3 w-3 shrink-0 sm:h-4 sm:w-4" />} {t.draw.download}
        </button>
        <button onClick={() => act("print")} className="h-9 w-8 min-w-0 btn-pop btn-pop-sm sticker-sm inline-flex items-center justify-center rounded-full bg-sky text-white px-1 sm:h-auto sm:w-auto sm:px-3 sm:py-2" aria-label={t.draw.print} data-testid={`coloring-print-${item.id}`}>
          {busy === "print" ? <Loader2 className="h-3 w-3 animate-spin sm:h-4 sm:w-4" /> : <Printer className="h-3 w-3 sm:h-4 sm:w-4" />}
        </button>
      </div>
    </motion.article>
  );
};

export const ColoringGrid = ({ limit, items: given }) => {
  const { t, lang } = useI18n();
  const { coloring, user } = useSite();
  const list = Array.isArray(given) ? given : Array.isArray(coloring) ? coloring : [];
  const items = limit ? list.slice(0, limit) : list;
  if (!items.length) return <p className="mt-10 font-bold" data-testid="coloring-empty">{t.draw.empty}</p>;
  return (
    <>
      {!user && (
        <p className="mt-8 inline-flex items-center gap-2 rounded-full bg-white sticker-sm px-4 py-2 text-sm font-extrabold" data-testid="coloring-locked-note">
          <Lock className="w-4 h-4" /> {t.draw.locked}
        </p>
      )}
      <div className="mt-12 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="coloring-grid">
        {items.map((it, i) => <ColoringCard key={it.id} item={it} i={i} user={user} t={t} lang={lang} />)}
      </div>
    </>
  );
};
